import * as React from 'react';

import { captureException } from '@sentry/react';

import { setLocation } from 'js/lib/coursera.redirect';
import { useLocation, useNavigate } from 'js/lib/useRouter';
import { isAuthenticatedUser } from 'js/lib/user';

import { Button } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { useEnterpriseLearningPathCurriculum } from 'bundles/enterprise-learning-path-description-page/contexts/EnterpriseLearningPathCurriculumContext';
import {
  getEnrollingCourseActionId,
  getEnrollingCourseAssessmentActionId,
} from 'bundles/enterprise-learning-path-description-page/contexts/SkillPathwayActivelyEnrollingContext';
import usePathwayAndCourseEnrollmentActions from 'bundles/enterprise-learning-path-description-page/hooks/usePathwayAndCourseEnrollmentActions';
import useGetSkillPathwayCourseStartLink from 'bundles/skills-common/shared/verified-skills/hooks/useGetSkillPathwayCourseStartLink';
import { useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';

import _t from 'i18n!nls/enterprise-learning-path-description-page';

type Props = {
  isEnrolledInPathway: boolean;
  isEnrolledInCourse: boolean;
  isProgramMember: boolean;
  courseId: string;
  courseSlug: string;
  programId: string;
  redirectToAssessment?: boolean;
  pathwayCollectionId?: string;
  pathwayId?: string;
  programSlug?: string;
  thirdPartyOrganizationSlug?: string;
  activateProgramAccessModal?: () => void;
  // isEnrollingCallback and disabled are used when multiple buttons that perform enrollment actions are present on the page.
  isEnrollingCallback?: (currentEnrollingAction: string | undefined) => void;
  disabled?: boolean;
  hiddenButton?: boolean;
  // onClickHandler callback is used when hiddenButton is true to expose the onClick handler to parent components
  onClickHandler?: (onClick: () => Promise<void>) => void;
  // customRedirectUrl is used to override the default redirect URL after successful enrollment
  customRedirectUrl?: string;
};

// This button will handle enrolling the user into learning path and course if user is missing access.
// If redirecting to assessment, the button will fetch the assessment ID after user enrolls in course as enrollment is required for this item.
const EnterpriseSkillPathwayCourseActionButton = ({
  isEnrolledInPathway,
  isEnrolledInCourse,
  isProgramMember,
  pathwayCollectionId,
  pathwayId,
  courseId,
  programId,
  courseSlug,
  activateProgramAccessModal,
  programSlug,
  thirdPartyOrganizationSlug,
  isEnrollingCallback,
  disabled,
  redirectToAssessment,
  hiddenButton = false,
  onClickHandler,
  customRedirectUrl,
}: Props) => {
  const navigate = useNavigate();
  const program = useProgram();
  const thirdPartyOrganization = useThirdPartyOrganization();
  const location = useLocation();
  const [isEnrolling, setIsEnrolling] = React.useState(false);
  const [isNavigatingToAssessment, setIsNavigatingToAssessment] = React.useState(false);
  const { executeEnrollments } = usePathwayAndCourseEnrollmentActions();
  const { fetchSkillPathwayCourseStartLink, fetchSkillPathwayCourseFirstIncompleteAssessment } =
    useGetSkillPathwayCourseStartLink(courseId, courseSlug);
  const { refetch: refetchCurriculum } = useEnterpriseLearningPathCurriculum();
  const track = useTracker();

  React.useEffect(() => {
    if (isEnrolling && redirectToAssessment) {
      isEnrollingCallback?.(getEnrollingCourseAssessmentActionId(courseId));
    } else if (isEnrolling) {
      isEnrollingCallback?.(getEnrollingCourseActionId(courseId));
    } else {
      isEnrollingCallback?.(undefined);
    }
  }, [isEnrolling, isEnrollingCallback, courseId, redirectToAssessment]);

  const onActionSuccess = React.useCallback<(useAssessmentRedirect?: boolean) => Promise<void>>(
    async (useAssessmentRedirect?: boolean) => {
      setIsNavigatingToAssessment(true);
      // Redirect on success, retain "isEnrolling" state as it continues to show loading state and prevent double click.
      if (customRedirectUrl) {
        setLocation(customRedirectUrl);
      } else if (useAssessmentRedirect) {
        // fetch the first incomplete assessment, some vsp courses may include multiple assessments
        const assessmentId = await fetchSkillPathwayCourseFirstIncompleteAssessment();

        if (assessmentId) {
          setLocation(`/learn/${courseSlug}/assignment-submission/${assessmentId}`);
        } else {
          // Fall back to course start link if no assessment found
          const skillPathwayCourseStartLink = await fetchSkillPathwayCourseStartLink();
          setLocation(skillPathwayCourseStartLink);
        }
      } else {
        const skillPathwayCourseStartLink = await fetchSkillPathwayCourseStartLink();
        setLocation(skillPathwayCourseStartLink);
      }
    },
    [courseSlug, fetchSkillPathwayCourseStartLink, fetchSkillPathwayCourseFirstIncompleteAssessment, customRedirectUrl]
  );

  const onClick = React.useCallback<() => Promise<void>>(async () => {
    track('click_button', {
      button: {
        name: redirectToAssessment
          ? 'skill_pathway_skill_area_take_assessment'
          : 'skill_pathway_skill_area_start_learning',
      },
      enterprise: {
        ...(thirdPartyOrganization?.slug ? { organizationSlug: thirdPartyOrganization?.slug } : {}),
        ...(program?.slug ? { programSlug: program?.slug } : {}),
      },
      product: {
        id: courseId,
        type: 'course',
      },
    });

    // Don't show enrolling state if already enrolled.
    if (!redirectToAssessment && isEnrolledInPathway && isEnrolledInCourse) {
      onActionSuccess();
      return;
    } else if (redirectToAssessment && isEnrolledInCourse && isEnrolledInPathway) {
      onActionSuccess(true);
      return;
    }

    if (!isAuthenticatedUser()) {
      navigate({ pathname: location.pathname, search: '?authMode=login' });
    } else if (!isProgramMember) {
      activateProgramAccessModal?.();
    } else {
      setIsEnrolling(true);

      try {
        await executeEnrollments({
          isEnrolledInPathway,
          isEnrolledInCourse,
          pathwayId,
          programId,
          courseId,
          pathwayCollectionId,
          programSlug,
          thirdPartyOrganizationSlug,
        });

        // Refetch curriculum data to ensure enrollment has propagated before proceeding
        // This ensures subsequent queries that require enrollment will have permission
        await refetchCurriculum();
        if (redirectToAssessment) {
          onActionSuccess(true);
        } else {
          onActionSuccess();
        }
      } catch (error) {
        captureException(error, {
          extra: { message: 'Error enrolling into skill pathway course' },
        });

        setIsEnrolling(false);
        // Since we don't have proper error states, reload page on failure to reset/refresh enrollment states (as we don't know what was a success or failure here).
        setLocation(location.pathname);
      }
    }
  }, [
    track,
    redirectToAssessment,
    thirdPartyOrganization?.slug,
    program?.slug,
    courseId,
    isEnrolledInPathway,
    isEnrolledInCourse,
    navigate,
    location.pathname,
    isProgramMember,
    activateProgramAccessModal,
    executeEnrollments,
    pathwayId,
    programId,
    pathwayCollectionId,
    programSlug,
    thirdPartyOrganizationSlug,
    onActionSuccess,
    refetchCurriculum,
  ]);

  if (hiddenButton && onClickHandler) {
    onClickHandler(onClick);
  }

  if (hiddenButton) {
    return null;
  }

  if (redirectToAssessment) {
    return (
      <Button
        onClick={onClick}
        disabled={disabled}
        size="small"
        loading={isEnrolling || isNavigatingToAssessment}
        variant="ghost"
      >
        {_t('Start assessment')}
      </Button>
    );
  } else {
    return (
      <Button
        onClick={onClick}
        disabled={disabled}
        size="small"
        loading={isEnrolling || isNavigatingToAssessment}
        variant="secondary"
      >
        {_t('Start learning')}
      </Button>
    );
  }
};

export default EnterpriseSkillPathwayCourseActionButton;
